import * as React from 'react';
import { useMemo, useState } from 'react';
import classnames from '../utils/classnames.tsx';
import Navigation, { useNavigation } from './Navigation';
import { useApplicationContext } from '../contexts/ApplicationContext.tsx';
import { LayoutContextProvider } from '../contexts/LayoutContext.tsx';
import useMediaQuery, { MOBILE_MEDIA_QUERY } from '../utils/hooks/useMediaQuery.ts';
import SupportBar from '../../support/components/SupportBar.tsx';
import { canUseSupportMode } from '../../users/utils.ts';
import AnnouncementBar from '../../announcements/components/AnnouncementBar.tsx';

interface LayoutProps {
    children?: any;
    className?: string;
    supportBarControls?: any;
}

const Layout = (props: LayoutProps) => {
    const { children, className, supportBarControls } = props;
    const { user, announcements } = useApplicationContext();

    // single breakpoint subscription for the whole shell; everything below reads it from the layout context
    const isMobile = useMediaQuery(MOBILE_MEDIA_QUERY);
    const navigationProps = useNavigation(isMobile);
    const supportBarVisible = canUseSupportMode(user);
    const hasNavigation = !!user?.id;

    const [subHeaderSlot, setSubHeaderSlot] = useState<HTMLElement | null>(null);
    const { onCollapsedChange } = navigationProps;
    const layoutContext = useMemo(
        () => ({
            isMobile,
            openNavigation: hasNavigation ? () => onCollapsedChange(false) : undefined,
            subHeaderSlot,
            setSubHeaderSlot,
        }),
        [isMobile, hasNavigation, onCollapsedChange, subHeaderSlot]
    );

    return (
        <LayoutContextProvider value={layoutContext}>
            {announcements?.length > 0 &&
                announcements.map((announcement) => (
                    <AnnouncementBar key={announcement.id} announcement={announcement} />
                ))}
            {supportBarVisible && <SupportBar user={user} controls={supportBarControls} />}
            <div
                className={classnames(
                    'flex h-full dark-mode-ready bg-surface-weak relative min-h-0 overflow-hidden',
                    className
                )}
            >
                {hasNavigation ? <Navigation {...navigationProps} /> : null}

                <div className="flex-1 flex flex-col md:flex-row h-full overflow-auto">{children}</div>
            </div>
        </LayoutContextProvider>
    );
};

export default Layout;
